import { isApplicationOpen } from "../../utils/seasonDates";
import { useState } from "react";
import { SeasonLink as Link } from "../../components/Seasons/SeasonLink";
import { useSeason } from "../../context/SeasonContext";
import { useAuth } from "../../context/AuthContext";
import { PublicHeader } from "../../components/Header/PublicHeader";
import {
  applyToSeason,
  SEASON_STATUS,
  PARTICIPATION_STATUS,
} from "../../api/seasons";
import styles from "../../components/Seasons/seasons.module.css";
const dateLabel = (value) =>
  value
    ? new Date(`${value}T12:00:00`).toLocaleDateString("ru-RU", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";
export function SeasonPublicLayout({ children }) {
  return (
    <div className={styles.publicLayout}>
      <PublicHeader accountPath="/my-season" />
      {children}
    </div>
  );
}
export function SeasonsPage() {
  const { seasons, withSeason } = useSeason();
  return (
    <main className={`${styles.page} ${styles.publicPage}`}>
      <div className={styles.hero}>
        <span className={styles.eyebrow}>ЮФУ.Про / Сезоны</span>
        <h1>
          Новый сезон.
          <br />
          Новые возможности.
        </h1>
        <p>
          Каждый интенсив — отдельная история. Выбирайте сезон, изучайте проекты
          и возвращайтесь к материалам прошлых лет.
        </p>
        <Link className={styles.secondary} to={withSeason("/")}>
          На главную
        </Link>
      </div>
      <div className={styles.grid}>
        {seasons.map((item) => (
          <article className={styles.card} key={item.id}>
            <span className={`${styles.status} ${styles[item.status]}`}>
              {SEASON_STATUS[item.status]}
            </span>
            <h2>{item.title}</h2>
            <p>{item.description || "Проекты, события и участники ЮФУ.Про."}</p>
            {item.starts_on && (
              <small>
                {dateLabel(item.starts_on)}
                {item.ends_on && ` — ${dateLabel(item.ends_on)}`}
              </small>
            )}
            <div className={styles.actions}>
              <Link
                className={styles.primary}
                to={withSeason(`/seasons/${item.slug}`, item.id)}
              >
                Открыть сезон →
              </Link>
              <Link
                className={styles.secondary}
                to={withSeason("/cases", item.id)}
              >
                Кейсы
              </Link>
            </div>
          </article>
        ))}
      </div>
    </main>
  );
}
export function MySeasonPage() {
  const { season, participation, mySeasons, refresh, withSeason } = useSeason();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const staff = ["ADMIN", "TRACK_COORDINATOR"].includes(user?.role);
  const accepted = participation?.status === "accepted";
  const pending = submitted || participation?.status === "applied";
  const completed = season.status === "completed";
  const canApply =
    !staff &&
    isApplicationOpen(season) &&
    (!participation || participation.status === "withdrawn");
  const apply = async () => {
    setBusy(true);
    setError("");
    try {
      await applyToSeason(season.id);
      setSubmitted(true);
      refresh();
    } catch (failure) {
      setError(failure.message);
    } finally {
      setBusy(false);
    }
  };
  const message = staff
    ? "Настройте набор, подготовьте материалы и управляйте участниками выбранного сезона."
    : completed
      ? "Сезон завершён. Здесь сохранены его кейсы, программа, команды и ваши работы."
      : accepted
        ? "Вы прошли отбор. Выбирайте проект, знакомьтесь с командой и следите за программой."
        : pending
          ? "Заявка принята. Результат отбора появится здесь — повторно отправлять заявку не нужно."
          : participation?.status === "rejected"
            ? "В этом сезоне отбор не пройден. Вы можете следить за новостями и подать заявку в следующий сезон."
            : season.status === "draft"
              ? "Команда готовит программу и проекты. Материалы ещё не опубликованы для участников."
              : canApply
                ? "Присоединяйтесь к интенсиву: подайте заявку, дождитесь отбора и выберите проект."
                : "Приём заявок закрыт. Изучайте проекты сезона и следите за анонсами следующего набора.";
  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <span className={styles.eyebrow}>
          Мой сезон / {SEASON_STATUS[season.status]}
        </span>
        <h1>{season.title}</h1>
        <p>{message}</p>
        <div className={styles.actions}>
          {staff && (
            <Link
              className={styles.primary}
              to={withSeason("/admin?section=overview")}
            >
              Управление сезоном →
            </Link>
          )}
          {canApply &&
            (season.application_url ? (
              <a
                className={styles.primary}
                href={season.application_url}
                target="_blank"
                rel="noreferrer"
              >
                Заполнить заявку ↗
              </a>
            ) : (
              <button
                className={styles.primary}
                disabled={busy}
                onClick={apply}
              >
                {busy ? "Отправляем…" : "Подать заявку"}
              </button>
            ))}
          {accepted && !completed && (
            <Link className={styles.primary} to={withSeason("/cases")}>
              Выбрать кейс →
            </Link>
          )}
          <Link className={styles.secondary} to={withSeason("/seasons")}>
            Все сезоны
          </Link>
        </div>
        {error && <p role="alert">{error}</p>}
      </section>
      <div className={styles.grid}>
        <article className={styles.card}>
          <small>Ваш статус</small>
          <h2>
            {submitted
              ? "Заявка на рассмотрении"
              : PARTICIPATION_STATUS[participation?.status] ||
                (user?.role === "ADMIN"
                  ? "Администратор"
                  : "Вы не подавали заявку")}
          </h2>
          <p>{participation?.track || "Направление пока не назначено"}</p>
        </article>
        <article className={styles.card}>
          <small>Сроки сезона</small>
          <h2>
            {season.starts_on ? dateLabel(season.starts_on) : "Даты уточняются"}
          </h2>
          <p>
            {season.ends_on
              ? `Завершение: ${dateLabel(season.ends_on)}`
              : "Точные даты появятся после публикации программы."}
            {season.applications_close_on && (
              <>
                <br />
                Заявки до {dateLabel(season.applications_close_on)}
              </>
            )}
          </p>
        </article>
      </div>
      <h2 className={styles.sectionTitle}>Всё для участия</h2>
      <div className={styles.steps}>
        {[
          ["/cases", "Витрина кейсов", "Проекты и выбор приоритетов"],
          ["/team", "Моя команда", "Состав, наставник и работа над проектом"],
          [
            "/schedule",
            "Программа и мои работы",
            "Занятия, задания и цифровой след",
          ],
          ["/news", "Новости сезона", "Анонсы, события и результаты"],
        ].map(([path, title, description], index) => (
          <Link className={styles.step} key={path} to={withSeason(path)}>
            <span>0{index + 1}</span>
            <div>
              <strong>{title}</strong>
              <p>{description}</p>
            </div>
            <span aria-hidden="true">↗</span>
          </Link>
        ))}
      </div>
      <h2 className={styles.sectionTitle}>Мои сезоны</h2>
      {mySeasons.length ? (
        <div className={styles.grid}>
          {mySeasons.map(({ season: item, participation: entry }) => (
            <Link
              className={styles.card}
              to={withSeason("/my-season", item.id)}
              key={item.id}
            >
              <span className={`${styles.status} ${styles[item.status]}`}>
                {SEASON_STATUS[item.status]}
              </span>
              <h3>{item.title}</h3>
              <p>{PARTICIPATION_STATUS[entry.status]}</p>
            </Link>
          ))}
        </div>
      ) : (
        <p>Здесь появится история участия после первой заявки.</p>
      )}
    </div>
  );
}

export function SeasonOverviewPage() {
  const { season, withSeason } = useSeason();
  return (
    <main className={`${styles.page} ${styles.publicPage}`}>
      <section className={styles.hero}>
        <span className={styles.eyebrow}>
          ЮФУ.Про / {SEASON_STATUS[season.status]}
        </span>
        <h1>{season.title}</h1>
        <p>
          {season.description ||
            "Проекты, люди и события проектного интенсива Южного федерального университета."}
        </p>
        <div className={styles.actions}>
          {isApplicationOpen(season) && (
            <Link className={styles.primary} to={withSeason("/my-season")}>
              Участвовать →
            </Link>
          )}
          <Link className={styles.secondary} to={withSeason("/seasons")}>
            Все сезоны
          </Link>
        </div>
      </section>
      <div className={styles.grid}>
        {[
          ["/cases", "Кейсы сезона", "Проектные задачи и решения команд"],
          ["/news", "Новости сезона", "Анонсы, события и итоги"],
          ["/#schedule-preview", "Программа", "Встречи и события интенсива"],
        ].map(([path, title, description]) => (
          <Link className={styles.card} to={withSeason(path)} key={path}>
            <h2>{title} ↗</h2>
            <p>{description}</p>
          </Link>
        ))}
      </div>
    </main>
  );
}
